/**
 * Example sentences for every spelling word.
 *
 * The sentence exists ONLY to help the student hear which word is being spoken
 * (for example, to tell "two" from "to" or "too"). It is read aloud by recorded
 * sentence audio when available, or by browser text-to-speech otherwise.
 *
 * Keep every sentence short, simple, and clear for a young child, no matter how
 * advanced the spelling word is. Sentence difficulty must NOT grow with level:
 * use everyday words, about 3 to 7 words, and include the spelling word exactly once.
 */
export const SPELLING_SENTENCES: Readonly<Record<string, string>> = {
  // Level 1: Short a CVC
  cat: "The fluffy cat naps quietly.",
  hat: "She likes her pink hat",
  sat: "The boy sat down",
  man: "The man wore a blue hat.",
  ran: "He quickly ran to school",
  map: "I found the treasure on the map.",
  nap: "I want to take a nap.",
  tap: "I heard a drip from the tap.",
  bag: "She put toys in her bag.",
  sad: "The puppy looks very sad.",
  // Level 2: Short i CVC
  sit: "The child can sit.",
  pit: "A small pit sits here.",
  hit: "The mallet can hit.",
  big: "The elephant is very big.",
  pig: "The pink pig smiles.",
  dig: "The child likes to dig.",
  pin: "The pin has a pink top.",
  win: "We can win the game!",
  bib: "The baby wears a bib.",
  kid: "I see a smiling kid.",
  // Level 3: Short o CVC
  hot: "The sun feels hot.",
  pot: "The orange pot has a lid.",
  not: "The rope is not loose.",
  dog: "The dog wears a blue collar.",
  log: "The log has golden rings.",
  hop: "I hop over the puddle.",
  top: "The colorful top spins fast.",
  box: "The box is made of wood.",
  fox: "The orange fox sits quietly.",
  mop: "I see a colorful mop.",
  // Level 4: Short u CVC
  sun: "The sun is up.",
  run: "I can run fast.",
  fun: "The game is fun.",
  cup: "The cup is red.",
  pup: "The pup can run.",
  bus: "The bus is big.",
  bug: "A bug is on a leaf.",
  hug: "I gave Mom a hug.",
  mud: "The pig is in mud.",
  cut: "I can cut paper.",
  // Level 5: Short e CVC
  bed: "I made my bed.",
  red: "I see a red hat.",
  hen: "The hen laid an egg.",
  pen: "I have a blue pen.",
  ten: "I have ten toes.",
  get: "I can get the ball.",
  net: "The net is full.",
  wet: "My socks are wet.",
  leg: "My leg got hurt.",
  jet: "The jet is in the sky.",
  // Level 6: Mixed short-vowel CVC
  cap: "He wears a red cap.",
  fin: "The fish has a fin.",
  cob: "I ate corn on the cob.",
  gum: "She chews pink gum.",
  web: "The spider made a web.",
  van: "The van is blue.",
  rip: "My shirt has a rip.",
  job: "Her job is to put out fires.",
  yes: "Mom said yes!",
  bud: "The flower has a bud.",
  // Level 7: First heart words
  the: "The dog ran home.",
  a: "I see a bird.",
  I: "I can jump high.",
  to: "We walk to school.",
  of: "I ate a bowl of soup.",
  you: "I love you.",
  was: "The baby was asleep.",
  for: "This gift is for you.",
  are: "We are friends.",
  said: "The duck said quack.",
  // Level 8: Final doubled consonants (-ll -ss -ff -zz)
  bell: "The bell rang.",
  well: "I feel well today.",
  hill: "We ran up the hill.",
  miss: "I miss my dog.",
  pass: "Please pass the salt.",
  off: "Turn off the light.",
  puff: "Blow a puff of air.",
  buzz: "Bees buzz.",
  fizz: "The soda has fizz.",
  jazz: "Dad likes jazz music.",
  // Level 9: Final -ck
  back: "Come back home.",
  pack: "Pack your bag.",
  sick: "I feel sick.",
  kick: "Kick the ball.",
  lock: "Lock the door.",
  rock: "I found a rock.",
  duck: "The duck swims.",
  luck: "Good luck!",
  neck: "A giraffe has a long neck.",
  deck: "We sat on the deck.",
  // Level 10: Final -ng and -nk
  sing: "I like to sing.",
  ring: "She has a gold ring.",
  long: "The snake is long.",
  song: "Sing a song.",
  hang: "Hang up your coat.",
  sink: "Wash your hands in the sink.",
  pink: "The pig is pink.",
  bank: "I put money in the bank.",
  junk: "The box is full of junk.",
  thank: "I thank my mom.",
  // Level 11: Heart words set 2
  he: "He is my brother.",
  she: "She is my sister.",
  they: "They are my friends.",
  have: "I have a cat.",
  one: "I have one nose.",
  two: "I have two hands.",
  what: "What is your name?",
  where: "Where is my hat?",
  come: "Come here, please.",
  some: "I want some milk.",
  // Level 12: Final blends -nd -nt -st -mp
  and: "I like cats and dogs.",
  hand: "Raise your hand.",
  tent: "We slept in a tent.",
  sent: "I sent a letter.",
  best: "You are my best friend.",
  nest: "The bird is in its nest.",
  jump: "I can jump.",
  lamp: "Turn on the lamp.",
  must: "You must wash your hands.",
  last: "I am last in line.",
  // Level 13: Open-syllable short words
  me: "Give it to me.",
  be: "I want to be a doctor.",
  go: "Let's go home.",
  no: "No, thank you.",
  so: "I am so happy.",
  hi: "Say hi to Grandma.",
  my: "This is my dog.",
  by: "Sit by me.",
  why: "Why is the sky blue?",
  we: "We like to play.",
  // Level 14: More final blends -lt -lf -lk -sk -ft
  belt: "I wear a belt.",
  melt: "Ice will melt.",
  self: "Be your true self.",
  milk: "I drink milk.",
  silk: "The scarf is made of silk.",
  desk: "I sit at my desk.",
  mask: "He wears a mask.",
  gift: "I got a gift.",
  left: "Turn left.",
  lift: "Lift the box.",
  // Level 15: Heart words set 3
  do: "What do you want?",
  who: "Who is at the door?",
  from: "This gift is from Mom.",
  were: "We were late.",
  once: "I went there once.",
  does: "Does it hurt?",
  done: "I am done.",
  any: "Do you have any milk?",
  many: "I have many toys.",
  very: "It is very cold.",
  // Level 16: Initial l-blends
  black: "The cat is black.",
  clap: "Clap your hands.",
  flag: "Wave the flag.",
  glad: "I am glad you came.",
  plan: "We have a plan.",
  slip: "Don't slip on the ice.",
  blob: "A blob of paint fell.",
  clip: "Clip the papers together.",
  flip: "Flip the pancake.",
  plus: "Two plus two is four.",
  // Level 17: Initial r-blends
  brim: "The cup is full to the brim.",
  crab: "A crab walks sideways.",
  drip: "I hear a drip.",
  frog: "The frog hops.",
  grin: "He has a big grin.",
  prim: "She looks prim and neat.",
  trap: "The mouse is in a trap.",
  brick: "The house is made of brick.",
  crop: "The farmer grows a crop.",
  drum: "I play the drum.",
  // Level 18: Initial s-blends
  scan: "Scan the page.",
  skip: "I like to skip.",
  smell: "I smell cookies.",
  snap: "Snap your fingers.",
  spin: "Spin the top.",
  stop: "Stop at the red light.",
  swim: "I can swim.",
  scab: "I have a scab on my knee.",
  snip: "Snip the string.",
  stem: "The flower has a stem.",
  // Level 19: Digraph sh
  ship: "The ship is on the sea.",
  shop: "We shop for food.",
  shut: "Shut the door.",
  dish: "Wash the dish.",
  fish: "The fish swims.",
  wish: "Make a wish.",
  cash: "I pay with cash.",
  rush: "Don't rush.",
  shed: "The tools are in the shed.",
  mash: "Mash the potatoes.",
  // Level 20: Digraph ch and tch
  chip: "I ate a chip.",
  chop: "Chop the wood.",
  chin: "Touch your chin.",
  much: "Thank you so much.",
  such: "It was such a fun day.",
  rich: "The king is rich.",
  catch: "Catch the ball.",
  match: "The socks match.",
  hatch: "The egg will hatch.",
  pitch: "Pitch the ball to me.",
  // Level 21: Digraph th
  thin: "The paper is thin.",
  thick: "The book is thick.",
  path: "Walk on the path.",
  bath: "I take a bath.",
  math: "I like math.",
  this: "This is my hat.",
  that: "That is my bike.",
  then: "We ate, then we played.",
  them: "I see them.",
  with: "Come with me.",
  // Level 22: Digraphs wh and qu
  when: "When is lunch?",
  which: "Which one do you want?",
  whip: "Whip the cream.",
  whiz: "She is a math whiz.",
  quiz: "We have a quiz today.",
  quit: "Never quit.",
  quick: "Be quick!",
  quack: "The duck says quack.",
  quilt: "The quilt is warm.",
  quite: "It is quite cold.",
  // Level 23: Silent-e long a (a_e)
  cake: "I ate cake.",
  name: "What is your name?",
  late: "Don't be late.",
  made: "I made a card.",
  gate: "Open the gate.",
  same: "We have the same shoes.",
  take: "Take a cookie.",
  gave: "She gave me a hug.",
  lake: "We swim in the lake.",
  fade: "The colors fade in the sun.",
  // Level 24: Silent-e long i (i_e)
  like: "I like pizza.",
  time: "What time is it?",
  five: "I am five.",
  ride: "I ride my bike.",
  bike: "My bike is red.",
  hide: "Let's hide.",
  line: "Stand in line.",
  mine: "This toy is mine.",
  side: "Stand by my side.",
  smile: "Give me a smile.",
  // Level 25: Silent-e long o and u (o_e, u_e)
  home: "Let's go home.",
  hope: "I hope you win.",
  rope: "Pull the rope.",
  note: "Write a note.",
  those: "Those are my shoes.",
  use: "Use a pencil.",
  cute: "The puppy is cute.",
  huge: "The whale is huge.",
  rule: "Follow the rule.",
  flute: "She plays the flute.",
  // Level 26: Silent-e long e and mixed CVCe
  these: "These are my books.",
  theme: "The party has a fun theme.",
  Pete: "Pete is my friend.",
  grape: "I ate a grape.",
  shine: "The stars shine.",
  stone: "I found a smooth stone.",
  prune: "A prune is a dried plum.",
  scene: "The movie had a funny scene.",
  complete: "The puzzle is complete.",
  eve: "Tonight is New Year's Eve.",
  // Level 27: Soft c and soft g
  cent: "I found one cent.",
  cell: "A cell is very small.",
  city: "I live in a city.",
  ice: "The ice is cold.",
  race: "I won the race.",
  gem: "The ring has a gem.",
  gym: "We play in the gym.",
  giant: "The giant is very tall.",
  page: "Turn the page.",
  age: "What is your age?",
  // Level 28: -dge and -ge for /j/
  edge: "Don't stand near the edge.",
  badge: "The officer has a badge.",
  fudge: "I love fudge.",
  judge: "The judge is fair.",
  bridge: "We walked over the bridge.",
  large: "The box is large.",
  charge: "Charge the phone.",
  strange: "That is a strange bug.",
  change: "Change your shoes.",
  range: "We can see a mountain range.",
  // Level 29: Contractions
  "can't": "I can't find my shoe.",
  "don't": "I don't like rain.",
  "isn't": "It isn't cold.",
  "didn't": "I didn't do it.",
  "I'll": "I'll help you.",
  "we'll": "We'll be there soon.",
  "that's": "That's my dog.",
  "you're": "You're my friend.",
  "they're": "They're at the park.",
  "won't": "I won't be late.",
  // Level 30: Heart words set 4
  friend: "You are my friend.",
  because: "I ate because I was hungry.",
  again: "Do it again.",
  could: "Could you help me?",
  would: "Would you like a cookie?",
  should: "You should rest.",
  people: "Many people came.",
  pretty: "The flower is pretty.",
  busy: "Mom is busy.",
  only: "I have only one shoe.",
  // Level 31: Long a teams ai / ay
  rain: "I like the rain.",
  train: "The train is fast.",
  wait: "Please wait for me.",
  paid: "I paid for lunch.",
  paint: "I like to paint.",
  play: "Let's play outside.",
  day: "It is a sunny day.",
  stay: "Stay with me.",
  way: "This is the way home.",
  spray: "Spray the plants.",
  // Level 32: Long e teams ee / ea
  see: "I see a bird.",
  tree: "The tree is tall.",
  green: "The grass is green.",
  sleep: "I sleep in my bed.",
  keep: "Keep the change.",
  eat: "I eat lunch.",
  read: "I like to read books.",
  clean: "My room is clean.",
  each: "Take one each.",
  dream: "I had a good dream.",
  // Level 33: Long o teams oa / ow
  boat: "The boat floats.",
  coat: "Put on your coat.",
  road: "The road is long.",
  soap: "Wash with soap.",
  toast: "I ate toast.",
  snow: "I like snow.",
  grow: "Plants grow.",
  show: "Show me your drawing.",
  yellow: "The sun is yellow.",
  window: "Look out the window.",
  // Level 34: Long i teams igh / ie / y
  night: "Good night.",
  light: "Turn on the light.",
  right: "Turn right.",
  high: "The bird flies high.",
  bright: "The sun is bright.",
  pie: "I ate apple pie.",
  tie: "Tie your shoes.",
  cry: "Babies cry.",
  fly: "Birds fly.",
  sky: "The sky is blue.",
  // Level 35: Long /oo/ teams oo / ew / ue
  moon: "The moon is bright.",
  food: "I like food.",
  soon: "See you soon.",
  room: "This is my room.",
  school: "I go to school.",
  new: "I have new shoes.",
  few: "I have a few toys.",
  grew: "The plant grew.",
  blue: "The sky is blue.",
  true: "That is true.",
  // Level 36: Short oo as in book
  book: "I read a book.",
  look: "Look at me.",
  good: "You did a good job.",
  wood: "The table is made of wood.",
  stood: "I stood up.",
  foot: "I hurt my foot.",
  cook: "Mom likes to cook.",
  hook: "Hang it on the hook.",
  shook: "The dog shook.",
  wool: "My hat is made of wool.",
  // Level 37: R-controlled ar
  car: "The car is red.",
  far: "The store is far away.",
  farm: "Cows live on the farm.",
  park: "We play at the park.",
  dark: "It is dark at night.",
  start: "Let's start.",
  hard: "The rock is hard.",
  yard: "We play in the yard.",
  sharp: "The knife is sharp.",
  March: "My birthday is in March.",
  // Level 38: R-controlled or / ore / oar
  more: "I want more.",
  store: "We went to the store.",
  horse: "I rode a horse.",
  short: "The pencil is short.",
  north: "Birds fly north.",
  born: "The baby was born today.",
  roar: "Lions roar.",
  board: "Write on the board.",
  four: "I have four cats.",
  door: "Close the door.",
  // Level 39: R-controlled er / ir / ur
  her: "I like her hat.",
  verb: "Run is a verb.",
  after: "We play after school.",
  first: "I am first in line.",
  bird: "The bird sings.",
  girl: "The girl is tall.",
  turn: "It is my turn.",
  burn: "Don't burn your hand.",
  hurt: "My knee hurt.",
  church: "We go to church.",
  // Level 40: Diphthong oi / oy
  oil: "Cook with oil.",
  boil: "Boil the water.",
  coin: "I found a coin.",
  join: "Come join us.",
  point: "Don't point.",
  noise: "I hear a noise.",
  boy: "The boy runs.",
  toy: "I have a new toy.",
  enjoy: "I enjoy books.",
  destroy: "Don't destroy the sandcastle.",
  // Level 41: Diphthong ou / ow as /ow/
  out: "Go out and play.",
  shout: "Don't shout.",
  cloud: "The cloud is white.",
  found: "I found my shoe.",
  house: "This is my house.",
  now: "Come here now.",
  how: "How are you?",
  down: "Sit down.",
  brown: "The dog is brown.",
  flower: "The flower is red.",
  // Level 42: Vowel au / aw / al
  saw: "I saw a bird.",
  draw: "I like to draw.",
  straw: "Drink with a straw.",
  paw: "The dog hurt its paw.",
  cause: "What was the cause?",
  pause: "Pause the movie.",
  walk: "Let's go for a walk.",
  talk: "Let's talk.",
  small: "The mouse is small.",
  ball: "Throw the ball.",
  // Level 43: Silent letters kn wr gn mb
  know: "I know the answer.",
  knee: "I hurt my knee.",
  knife: "Cut it with a knife.",
  write: "I can write my name.",
  wrong: "That answer is wrong.",
  wrap: "Wrap the gift.",
  gnat: "A gnat is a tiny bug.",
  sign: "Stop at the sign.",
  lamb: "The lamb is soft.",
  thumb: "I hurt my thumb.",
  // Level 44: ea as short e; common ie
  bread: "I ate bread.",
  head: "Put a hat on your head.",
  ready: "Are you ready?",
  heavy: "The box is heavy.",
  instead: "Have milk instead.",
  piece: "Have a piece of cake.",
  field: "Cows are in the field.",
  chief: "The fire chief came.",
  brief: "The talk was brief.",
  niece: "My niece is two.",
  // Level 45: Homophones set 1
  too: "I want to come too.",
  there: "Put it over there.",
  their: "That is their house.",
  your: "Is this your hat?",
  its: "The dog wags its tail.",
  hear: "I can hear you.",
  here: "Come here.",
  sea: "Fish live in the sea.",
  dear: "You are my dear friend.",
  deer: "A deer ran by.",
  // Level 46: Plurals -s and -es
  boxes: "I have two boxes.",
  foxes: "The foxes ran.",
  dishes: "Wash the dishes.",
  wishes: "I made three wishes.",
  lunches: "We packed our lunches.",
  classes: "I have two classes today.",
  buses: "The buses are yellow.",
  cities: "I have been to many cities.",
  babies: "The babies are asleep.",
  families: "Many families came.",
  // Level 47: Inflected ending -ed (three sounds)
  jumped: "The frog jumped.",
  helped: "I helped Mom.",
  asked: "I asked a question.",
  played: "We played outside.",
  called: "Grandma called me.",
  rained: "It rained all day.",
  wanted: "I wanted a cookie.",
  needed: "I needed help.",
  ended: "The movie ended.",
  started: "The game started.",
  // Level 48: Inflected ending -ing and the doubling rule
  running: "I like running.",
  sitting: "I am sitting down.",
  hitting: "Stop hitting the drum.",
  hopping: "The bunny is hopping.",
  shopping: "We went shopping.",
  dropped: "I dropped my cup.",
  stopped: "The car stopped.",
  planned: "We planned a party.",
  grabbed: "I grabbed my coat.",
  swimming: "I like swimming.",
  // Level 49: Drop silent e before -ing / -ed
  making: "I am making a card.",
  taking: "I am taking a nap.",
  riding: "I am riding my bike.",
  hoping: "I am hoping for snow.",
  using: "I am using a pencil.",
  smiled: "She smiled at me.",
  closed: "I closed the door.",
  named: "I named my cat Tom.",
  lived: "We lived here.",
  saved: "I saved my money.",
  // Level 50: Change y to i
  tried: "I tried my best.",
  cried: "The baby cried.",
  fried: "I like fried eggs.",
  carried: "I carried the bag.",
  studied: "I studied for the test.",
  happier: "I am happier today.",
  funnier: "This joke is funnier.",
  ladies: "The ladies smiled.",
  puppies: "The puppies are cute.",
  copies: "Make two copies.",
  // Level 51: Compound words
  sunshine: "I like the sunshine.",
  rainbow: "I see a rainbow.",
  backpack: "Put it in your backpack.",
  bedroom: "This is my bedroom.",
  football: "We play football.",
  notebook: "Write in your notebook.",
  cupcake: "I ate a cupcake.",
  firefly: "A firefly glows.",
  snowman: "We made a snowman.",
  mailbox: "The letter is in the mailbox.",
  // Level 52: Open and closed two-syllable words
  napkin: "Use a napkin.",
  basket: "The apples are in a basket.",
  picnic: "We had a picnic.",
  magnet: "The magnet sticks.",
  rabbit: "The rabbit hops.",
  tiger: "The tiger has stripes.",
  pilot: "The pilot flies the plane.",
  music: "I like music.",
  open: "Open the door.",
  begin: "Let's begin.",
  // Level 53: VCCV syllable juncture
  happen: "What will happen?",
  sudden: "There was a sudden noise.",
  better: "I feel better.",
  letter: "I wrote a letter.",
  dinner: "It is time for dinner.",
  summer: "It is hot in summer.",
  butter: "I like butter on toast.",
  pepper: "Add some pepper.",
  cotton: "My shirt is cotton.",
  muffin: "I ate a muffin.",
  // Level 54: VCV syllable juncture
  human: "I am a human.",
  silent: "The room was silent.",
  moment: "Wait a moment.",
  broken: "My toy is broken.",
  chosen: "You have been chosen.",
  cabin: "We stayed in a cabin.",
  lemon: "The lemon is sour.",
  robin: "A robin sang.",
  finish: "I will finish my work.",
  never: "I never give up.",
  // Level 55: Final stable syllable -le
  table: "Set the table.",
  apple: "I ate an apple.",
  little: "The bug is little.",
  middle: "Sit in the middle.",
  bottle: "Fill the bottle.",
  candle: "Light the candle.",
  simple: "This is simple.",
  purple: "I like purple.",
  jungle: "Monkeys live in the jungle.",
  eagle: "The eagle flies.",
  // Level 56: Homophones set 2
  ate: "I ate lunch.",
  eight: "I am eight years old.",
  bare: "My feet are bare.",
  bear: "The bear is big.",
  steal: "Do not steal.",
  steel: "The pot is made of steel.",
  hour: "Wait one hour.",
  our: "This is our house.",
  waste: "Don't waste food.",
  waist: "The belt goes around my waist.",
  // Level 57: Prefixes un- re- pre- dis-
  unhappy: "The boy is unhappy.",
  unkind: "Don't be unkind.",
  unfair: "That is unfair.",
  redo: "I will redo my work.",
  rewrite: "Please rewrite your name.",
  reread: "I will reread the book.",
  preview: "Watch the preview.",
  preheat: "Preheat the oven.",
  disagree: "I disagree.",
  dislike: "I dislike rain.",
  // Level 58: Suffixes -ful -less -ness -ly
  helpful: "You are helpful.",
  careful: "Be careful.",
  hopeful: "I am hopeful.",
  homeless: "We fed a homeless cat.",
  fearless: "The knight is fearless.",
  kindness: "Show kindness.",
  sadness: "I felt sadness.",
  slowly: "Walk slowly.",
  quickly: "Run quickly.",
  finally: "We are finally home.",
  // Level 59: Comparative -er / -est
  bigger: "My dog is bigger.",
  biggest: "This is the biggest cake.",
  hotter: "Today is hotter.",
  hottest: "This is the hottest day.",
  safer: "Walking is safer.",
  safest: "This is the safest place.",
  nicer: "You are nicer than me.",
  nicest: "She is the nicest girl.",
  happiest: "I am the happiest kid.",
  earliest: "I got up the earliest.",
  // Level 60: Irregular plurals and odd past tenses
  children: "The children play.",
  mice: "The mice ran.",
  teeth: "Brush your teeth.",
  feet: "Wash your feet.",
  women: "The women talked.",
  men: "The men worked.",
  geese: "The geese fly.",
  leaves: "The leaves fall.",
  wolves: "The wolves howl.",
  knives: "The knives are sharp.",
  // Level 61: Unaccented final -er / -or / -ar
  teacher: "My teacher is kind.",
  farmer: "The farmer has cows.",
  dollar: "I have one dollar.",
  collar: "The dog has a red collar.",
  sugar: "Add some sugar.",
  doctor: "The doctor helps me.",
  actor: "The actor is funny.",
  color: "What is your favorite color?",
  favor: "Do me a favor.",
  motor: "The boat has a motor.",
  // Level 62: Unaccented final -le / -el / -al / -il
  travel: "We like to travel.",
  camel: "A camel has a hump.",
  model: "I built a model car.",
  level: "I beat the level.",
  final: "This is the final game.",
  total: "What is the total?",
  animal: "A dog is an animal.",
  pencil: "I need a pencil.",
  fossil: "I found a fossil.",
  evil: "The evil witch laughed.",
  // Level 63: Unaccented final -en / -on / -in / -ain
  garden: "We grow food in the garden.",
  often: "I often read.",
  kitten: "The kitten is soft.",
  person: "She is a kind person.",
  season: "Fall is my favorite season.",
  reason: "What is the reason?",
  cousin: "My cousin is here.",
  captain: "The captain steers the ship.",
  mountain: "The mountain is tall.",
  fountain: "Drink from the fountain.",
  // Level 64: Three-syllable closed/open mixes
  fantastic: "You did a fantastic job.",
  remember: "I remember you.",
  together: "Let's play together.",
  hospital: "The doctor works at the hospital.",
  energy: "I have lots of energy.",
  library: "We go to the library.",
  tomorrow: "See you tomorrow.",
  calendar: "Look at the calendar.",
  umbrella: "Take an umbrella.",
  potato: "I ate a potato.",
  // Level 65: Prefixes mis- non- over- under- mid-
  misspell: "Don't misspell your name.",
  misplace: "I misplace my keys.",
  nonstop: "It rained nonstop.",
  nonprofit: "She works for a nonprofit.",
  overeat: "Don't overeat.",
  overdue: "The library book is overdue.",
  underline: "Underline the word.",
  underwater: "Fish live underwater.",
  midnight: "It is midnight.",
  midyear: "We have a midyear test.",
  // Level 66: Suffixes -ment -tion early look / -sion intro
  payment: "I made a payment.",
  movement: "I saw movement in the grass.",
  statement: "That is a true statement.",
  excitement: "I felt excitement.",
  action: "I like action movies.",
  nation: "Our nation is big.",
  station: "The train is at the station.",
  vacation: "We went on vacation.",
  fiction: "I like to read fiction.",
  section: "Read this section.",
  // Level 67: Suffixes -able / first -ible contrast
  readable: "Your writing is readable.",
  usable: "This pen is still usable.",
  lovable: "My puppy is lovable.",
  comfortable: "The bed is comfortable.",
  valuable: "The ring is valuable.",
  breakable: "The glass is breakable.",
  possible: "Anything is possible.",
  terrible: "The storm was terrible.",
  visible: "The moon is visible.",
  horrible: "The smell is horrible.",
  // Level 68: Suffixes -ous / -ious / -al
  famous: "She is a famous singer.",
  dangerous: "Fire is dangerous.",
  nervous: "I feel nervous.",
  curious: "The cat is curious.",
  serious: "This is serious.",
  various: "We saw various birds.",
  national: "It is a national park.",
  personal: "This is my personal diary.",
  natural: "The lake is natural.",
  usual: "I ate my usual lunch.",
  // Level 69: Homophones set 3 (often-missed academic pairs)
  accept: "I accept your gift.",
  except: "Everyone came except Sam.",
  affect: "Rain can affect our game.",
  effect: "The medicine had a good effect.",
  than: "I am taller than you.",
  weather: "The weather is sunny.",
  whether: "I don't know whether to go.",
  witch: "The witch has a black hat.",
  principle: "Honesty is a good principle.",
  principal: "The principal runs the school.",
  // Level 70: Silent-letter review in longer words
  unknown: "The answer is unknown.",
  knowledge: "Books give us knowledge.",
  written: "I have written a story.",
  wrapper: "Throw away the wrapper.",
  climb: "I like to climb trees.",
  comb: "Comb your hair.",
  design: "I like the design.",
  assign: "The teacher will assign homework.",
  listen: "Listen to me.",
  whistle: "I can whistle.",
  // Level 71: ie / ei beyond the jingle
  believe: "I believe you.",
  achieve: "You can achieve your goal.",
  relief: "What a relief!",
  thief: "The thief ran away.",
  receive: "I receive a letter.",
  ceiling: "Look up at the ceiling.",
  deceive: "Do not deceive your friends.",
  neighbor: "My neighbor is nice.",
  weigh: "I weigh the apples.",
  weird: "That bug looks weird.",
  // Level 72: ough family
  though: "I went, though it rained.",
  although: "I smiled although I was tired.",
  thought: "I thought of you.",
  bought: "I bought a book.",
  brought: "I brought my lunch.",
  fought: "The cats fought.",
  enough: "I have enough.",
  rough: "The rock is rough.",
  tough: "The meat is tough.",
  through: "Walk through the door.",
  // Level 73: Words with ph = /f/ and ch = /k/
  phone: "Mom is on the phone.",
  photo: "Take a photo.",
  alphabet: "I know the alphabet.",
  chorus: "We sang the chorus.",
  echo: "I hear an echo.",
  anchor: "Drop the anchor.",
  chemist: "The chemist mixes liquids.",
  character: "I like that character.",
  scheme: "He had a sneaky scheme.",
  orchid: "The orchid is purple.",
  // Level 74: Doubling in two-syllable words (accent matters)
  beginning: "This is the beginning.",
  forgetting: "I keep forgetting my hat.",
  occurred: "An idea occurred to me.",
  referred: "My friend referred me.",
  admitted: "He admitted he was wrong.",
  offered: "She offered me a cookie.",
  visited: "We visited Grandma.",
  entered: "I entered the room.",
  limited: "We have limited time.",
  happened: "What happened?",
  // Level 75: Commonly misspelled grade 5 words
  separate: "Keep them separate.",
  definitely: "I will definitely come.",
  probably: "It will probably rain.",
  really: "I really like you.",
  until: "Wait until I come back.",
  favorite: "Blue is my favorite color.",
  different: "We look different.",
  interesting: "This book is interesting.",
  surprise: "It is a surprise.",
  beautiful: "The flower is beautiful.",
  // Level 76: Latin root port (carry)
  portable: "The radio is portable.",
  transport: "Trucks transport food.",
  import: "We import fruit.",
  export: "We export cars.",
  report: "I wrote a report.",
  support: "Thank you for your support.",
  airport: "We flew from the airport.",
  passport: "I need my passport.",
  portal: "Step through the portal.",
  opportunity: "This is a great opportunity.",
  // Level 77: Latin root tract / duc / duct
  tractor: "The farmer drives a tractor.",
  attract: "Flowers attract bees.",
  subtract: "Subtract two from five.",
  contract: "Sign the contract.",
  conduct: "Your conduct was good.",
  educate: "Teachers educate kids.",
  produce: "Cows produce milk.",
  introduce: "Let me introduce my friend.",
  reduce: "Reduce your waste.",
  product: "This is a new product.",
  // Level 78: Latin root scrib / script and graph
  scribble: "Don't scribble on the wall.",
  describe: "Describe your dog.",
  inscribe: "Inscribe your name on the ring.",
  script: "Read the script.",
  manuscript: "The author sent her manuscript.",
  graphic: "Look at the graphic.",
  photograph: "I took a photograph.",
  autograph: "Can I have your autograph?",
  paragraph: "Read the first paragraph.",
  graphite: "Pencils have graphite.",
  // Level 79: Latin root spect / vis / vid
  inspect: "Inspect the car.",
  respect: "Show respect.",
  suspect: "The police caught the suspect.",
  spectator: "I am a spectator at the game.",
  vision: "I have good vision.",
  visit: "Come visit me.",
  video: "Watch the video.",
  provide: "We provide food.",
  visual: "It is a visual clue.",
  revise: "Revise your story.",
  // Level 80: Latin root dict / ject / rupt
  dictate: "I will dictate a letter.",
  predict: "Predict the weather.",
  dictionary: "Look it up in the dictionary.",
  object: "What is that object?",
  reject: "Don't reject my idea.",
  project: "I made a science project.",
  erupt: "The volcano will erupt.",
  interrupt: "Don't interrupt.",
  rupture: "The pipe may rupture.",
  bankrupt: "The store went bankrupt.",
  // Level 81: Greek roots phon / photo / tele / micro
  telephone: "The telephone rang.",
  microphone: "Speak into the microphone.",
  symphony: "We heard a symphony.",
  phonics: "We learn phonics.",
  photography: "I like photography.",
  telegraph: "They sent a telegraph.",
  telescope: "Look through the telescope.",
  microscope: "Look in the microscope.",
  microwave: "Heat it in the microwave.",
  telephoto: "He used a telephoto lens.",
  // Level 82: Number prefixes mono uni bi tri oct
  monotone: "He spoke in a monotone.",
  monologue: "The actor gave a monologue.",
  unicorn: "The unicorn has one horn.",
  uniform: "I wear a uniform.",
  bicycle: "I ride my bicycle.",
  bilingual: "She is bilingual.",
  triangle: "A triangle has three sides.",
  tripod: "The camera is on a tripod.",
  octopus: "The octopus has eight arms.",
  octagon: "A stop sign is an octagon.",
  // Level 83: Assimilated prefix in- (il- im- ir-)
  incomplete: "My work is incomplete.",
  incorrect: "That answer is incorrect.",
  invisible: "The air is invisible.",
  illegal: "Stealing is illegal.",
  illogical: "That idea is illogical.",
  impossible: "Nothing is impossible.",
  impatient: "Don't be impatient.",
  imperfect: "My drawing is imperfect.",
  irregular: "The shape is irregular.",
  irresponsible: "That was irresponsible.",
  // Level 84: Assimilated prefixes com- / ad- / sub-
  collect: "I collect rocks.",
  correct: "That is correct.",
  combine: "Combine the colors.",
  announce: "I will announce the winner.",
  appear: "Stars appear at night.",
  arrive: "We arrive at noon.",
  attach: "Attach the photo.",
  succeed: "You will succeed.",
  suffix: "Add a suffix.",
  suggest: "I suggest we rest.",
  // Level 85: -tion / -sion / -cian contrast
  attention: "Pay attention.",
  invention: "The light bulb is an invention.",
  direction: "Which direction is home?",
  decision: "I made a decision.",
  explosion: "We heard an explosion.",
  division: "We learned division.",
  musician: "The musician plays guitar.",
  magician: "The magician did a trick.",
  physician: "The physician helped me.",
  electrician: "The electrician fixed the light.",
  // Level 86: -ence / -ance and -ent / -ant
  difference: "What is the difference?",
  patience: "Have patience.",
  patient: "Be patient.",
  importance: "Sleep is of great importance.",
  evident: "The answer is evident.",
  distance: "The store is a short distance away.",
  distant: "We saw a distant star.",
  absence: "I noticed your absence.",
  absent: "He is absent today.",
  silence: "There was silence.",
  // Level 87: Schwa in the middle of longer words
  category: "Pick a category.",
  definite: "I need a definite answer.",
  desperate: "The dog was desperate for food.",
  privilege: "It is a privilege to be here.",
  similar: "Our shirts are similar.",
  opinion: "What is your opinion?",
  original: "This is the original.",
  president: "The president gave a speech.",
  secretary: "The secretary answered the phone.",
  excellent: "You did an excellent job.",
  // Level 88: Grade 6 academic workhorses
  recommend: "I recommend this book.",
  guarantee: "I guarantee it.",
  foreign: "I speak a foreign language.",
  height: "What is your height?",
  rhythm: "Clap to the rhythm.",
  vacuum: "Vacuum the floor.",
  license: "Dad has a driver's license.",
  anxious: "I feel anxious.",
  conscience: "Listen to your conscience.",
  mischief: "The puppy is full of mischief.",
  // Level 89: Greek roots bio / geo / hydr / therm
  biology: "I like biology.",
  biosphere: "The Earth is our biosphere.",
  geology: "Geology is the study of rocks.",
  geode: "I cracked open a geode.",
  hydrant: "The dog sat by the hydrant.",
  hydrogen: "Water has hydrogen.",
  dehydrate: "Drink water so you don't dehydrate.",
  thermos: "My soup is in a thermos.",
  thermometer: "Check the thermometer.",
  thermal: "I wore thermal socks.",
  // Level 90: Greek roots chron / metr / path / phil
  chronic: "He has a chronic cough.",
  chronicle: "The book is a chronicle.",
  synchronize: "Let's synchronize our watches.",
  metric: "We use the metric system.",
  diameter: "Measure the diameter.",
  perimeter: "Find the perimeter.",
  sympathy: "I feel sympathy for you.",
  empathy: "Show empathy.",
  pathology: "She studies pathology.",
  philosophy: "I like philosophy.",
  // Level 91: Latin roots bene / mal / cred / fid
  benefit: "Exercise has a benefit.",
  beneficial: "Sleep is beneficial.",
  benign: "The lump was benign.",
  malice: "He did it without malice.",
  malicious: "That was a malicious lie.",
  malfunction: "The robot had a malfunction.",
  credit: "Give her credit.",
  incredible: "That was incredible.",
  confident: "I feel confident.",
  fidelity: "The dog showed fidelity.",
  // Level 92: Latin roots pend / pos / mit / miss
  pendant: "She wears a pendant.",
  depend: "You can depend on me.",
  suspend: "Suspend the rope from the tree.",
  position: "Take your position.",
  opposite: "Up is the opposite of down.",
  compose: "I compose music.",
  transmit: "The radio can transmit sound.",
  mission: "Our mission is to help.",
  dismiss: "The teacher will dismiss us.",
  permission: "Ask for permission.",
  // Level 93: Connected spelling-meaning families
  please: "Please sit down.",
  pleasant: "It is a pleasant day.",
  pleasure: "It is a pleasure to meet you.",
  resign: "He will resign from his job.",
  signal: "Wait for my signal.",
  signature: "Sign your signature here.",
  define: "Define the word.",
  definition: "Read the definition.",
  compete: "We compete in the race.",
  competition: "I won the competition.",
  // Level 94: More meaning families (vowel change across forms)
  nature: "I love nature.",
  naturalist: "The naturalist studies birds.",
  historic: "This is a historic house.",
  historical: "I read a historical book.",
  politics: "Dad talks about politics.",
  political: "It was a political speech.",
  economy: "The economy is growing.",
  economic: "We have economic news.",
  critic: "The critic liked the movie.",
  critical: "This step is critical.",
  // Level 95: Hard -able vs -ible and -ary vs -ery
  reliable: "My car is reliable.",
  available: "The book is available.",
  acceptable: "Your work is acceptable.",
  responsible: "I am responsible for my dog.",
  flexible: "The straw is flexible.",
  accessible: "The park is accessible.",
  vocabulary: "I learn new vocabulary.",
  stationery: "I wrote on nice stationery.",
  ordinary: "It was an ordinary day.",
  military: "He is in the military.",
  // Level 96: Double-consonant academic words
  possession: "This is my favorite possession.",
  profession: "What is your profession?",
  success: "The party was a success.",
  occasion: "It is a special occasion.",
  aggressive: "The dog is aggressive.",
  necessary: "Is it necessary?",
  embarrass: "Don't embarrass me.",
  occurrence: "Rain is a common occurrence.",
  accommodate: "The room can accommodate ten people.",
  millennium: "A millennium is a thousand years.",
  // Level 97: 7th–8th commonly missed school words
  argument: "We had an argument.",
  judgment: "Use good judgment.",
  acknowledgment: "She nodded in acknowledgment.",
  independent: "I am independent.",
  immediately: "Come here immediately.",
  unfortunately: "Unfortunately, it rained.",
  especially: "I love fruit, especially apples.",
  environment: "Protect the environment.",
  government: "The government makes laws.",
  temperature: "What is the temperature?",
  // Level 98: Science and social-studies vocabulary
  hypothesis: "I have a hypothesis.",
  organism: "A frog is an organism.",
  photosynthesis: "Plants use photosynthesis.",
  metamorphosis: "A caterpillar goes through metamorphosis.",
  atmosphere: "Earth has an atmosphere.",
  constitution: "We read the constitution.",
  democracy: "We live in a democracy.",
  legislature: "The legislature makes laws.",
  amendment: "They passed an amendment.",
  civilization: "Rome was a great civilization.",
  // Level 99: Dense prefixed academic words
  unprecedented: "The storm was unprecedented.",
  incomprehensible: "His words were incomprehensible.",
  unconstitutional: "The law was unconstitutional.",
  indiscriminate: "The storm caused indiscriminate damage.",
  reconciliation: "The friends had a reconciliation.",
  disintegration: "The wall faced slow disintegration.",
  misinterpretation: "It was a misinterpretation.",
  unpredictable: "The weather is unpredictable.",
  inconsiderate: "That was inconsiderate.",
  transformation: "What a transformation!",
  // Level 100: Grade 8 capstone list
  conscientious: "She is a conscientious student.",
  acquaintance: "He is an acquaintance.",
  maintenance: "The car needs maintenance.",
  perseverance: "Perseverance pays off.",
  questionnaire: "Fill out the questionnaire.",
  liaison: "She is our liaison.",
  bureaucracy: "The bureaucracy is slow.",
  psychological: "It was a psychological test.",
  phenomenon: "A rainbow is a phenomenon.",
  entrepreneur: "My mom is an entrepreneur.",
};
